import { Controller } from '@hotwired/stimulus'

// Спиннер на время запроса (компоненты button / link с spinner: true).
//
// Включается класс is-loading и показывается иконка загрузки. Когда включать
// и снимать:
// • Отправка через Turbo-форму — включаем по turbo:submit-start (а не по
//   клику: сабмит может отменить клиентская валидация), снимаем по
//   turbo:submit-end на этой форме.
// • Ссылка/кнопка, по которой Turbo сам ходит по сети (Drive/Frame и т.д.) —
//   ждём turbo:before-fetch-response или turbo:fetch-request-error на самом элементе
//   (Turbo вешает эти события на ссылку или форму, с которой ушёл запрос).
// • Обычный fetch() в своём коде — Turbo эти события не шлёт, поэтому после
//   завершения fetch нужно самому сообщить кнопке событием fetch-end:
//
//     fetch(url).then(...).finally(() => {
//       event.target.dispatchEvent(new CustomEvent('fetch-end', { bubbles: true }))
//     })
//
//   event.target — та кнопка/ссылка, по которой кликнули (передайте её из обработчика клика).
const SPINNER_HTML = `<span class="btn__spinner">
  <svg viewBox="0 0 33 33" xmlns="http://www.w3.org/2000/svg"><use href="#icon-loader"></use></svg>
</span>`

export default class TurboSubmitSpinnerController extends Controller {
  connect() {
    this._onClick = this._onClick.bind(this)
    this.element.addEventListener('click', this._onClick)
  }

  disconnect() {
    this.element.removeEventListener('click', this._onClick)
    this._clearEndListeners()
  }

  _onClick() {
    if (this.element.classList.contains('is-loading')) return

    const form = this.element.closest('form') || this.element.form
    if (form) {
      this._clearEndListeners()
      this._submitFormRef = form
      // Загрузку включаем не по клику, а когда отправка реально началась:
      // клиентская валидация (form-validation) сабмит отменяет, и кнопка
      // осталась бы заблокированной в загрузке навсегда.
      this._onStart = () => this._start()
      this._onEnd = () => this._stop()
      this._submitFormRef.addEventListener(
        'turbo:submit-start',
        this._onStart,
        {
          once: true
        }
      )
      this._submitFormRef.addEventListener('turbo:submit-end', this._onEnd, {
        once: true
      })
    } else {
      this._start()
      this._clearEndListeners()
      this._onFetchResponse = (event) => {
        if (event.target !== this.element) return
        this._stop()
      }
      this._onFetchError = (event) => {
        if (event.target !== this.element) return
        this._stop()
      }
      this._onFetchEnd = () => this._stop()
      this.element.addEventListener(
        'turbo:before-fetch-response',
        this._onFetchResponse,
        { once: true }
      )
      this.element.addEventListener(
        'turbo:fetch-request-error',
        this._onFetchError,
        { once: true }
      )
      this.element.addEventListener('fetch-end', this._onFetchEnd, {
        once: true
      })
    }
  }

  _start() {
    this._ensureSpinner()
    this.element.classList.add('is-loading')
  }

  _stop() {
    this.element.classList.remove('is-loading')
    this._clearEndListeners()
  }

  _clearEndListeners() {
    if (this._submitFormRef && this._onEnd) {
      this._submitFormRef.removeEventListener('turbo:submit-end', this._onEnd)
    }
    if (this._submitFormRef && this._onStart) {
      this._submitFormRef.removeEventListener(
        'turbo:submit-start',
        this._onStart
      )
    }
    this._submitFormRef = null
    this._onEnd = null
    this._onStart = null

    if (this._onFetchResponse) {
      this.element.removeEventListener(
        'turbo:before-fetch-response',
        this._onFetchResponse
      )
      this._onFetchResponse = null
    }
    if (this._onFetchError) {
      this.element.removeEventListener(
        'turbo:fetch-request-error',
        this._onFetchError
      )
      this._onFetchError = null
    }
    if (this._onFetchEnd) {
      this.element.removeEventListener('fetch-end', this._onFetchEnd)
      this._onFetchEnd = null
    }
  }

  _ensureSpinner() {
    if (this.element.querySelector('.btn__spinner')) return
    this.element.insertAdjacentHTML('beforeend', SPINNER_HTML)
  }
}
